import { useIsPreviewOrDemo } from "../../../contexts/IsPreviewOrDemoContext";
import {
  Configuration,
  StripeRegistryDetails,
} from "../../../resources/modelTypes";
import { themeTypographyResetStyles } from "../../../website/components/themeTypographyResetStyles";
import { selectors } from "../../cypressSelectors";
import { StripeRegistryProgressBar } from "../../shared-components/StripeRegistryProgressBar";
import { isStripeGoalMet } from "../../stripeUtils";
import { SectionComponentProps } from "../../types";
import { DisplayPhotoOrVideo } from "../DisplayPhotoOrVideo";
import { useGuestStripe } from "../GuestStripeContext";
import { useWebsiteWidth } from "../WebsiteWidthContext";
import CashRegistryDefaultImage from "../assets/cashRegistryDefaultSmall.webp";
import { StripeContributionModal } from "./StripeContributionModal";
import { css } from "@emotion/react";
import {
  Badge,
  Icon,
  LoadingSpinner,
  styleUtils,
  theme,
} from "@minted/minted-components";
import React, { memo, useCallback, useEffect, useState } from "react";

const styles = {
  card: (isMobile: boolean) => css`
    background-color: ${theme.colors.gray100};
    border: ${styleUtils.rem(1)} solid ${theme.colors.border};
    border-radius: ${styleUtils.rem(8)};
    color: ${theme.colors.textPrimary};
    position: relative;
    ${isMobile && `flex: 0 0 calc(50% - ${theme.spacing.x2});`}
    min-height: ${isMobile ? styleUtils.rem(208) : styleUtils.rem(332)};
    width: ${isMobile ? "100%" : styleUtils.rem(256)};
  `,
  disabledTitle: css`
    ${theme.typeStyles.titleMediumStyles};
    color: ${theme.colors.white};
    text-align: center;
  `,
  disabledBodyText: css`
    ${theme.typeStyles.bodySmallStyles};
    color: ${theme.colors.white};
    text-align: center;
  `,
  disabledMessageWrapper: css`
    align-content: center;
    display: grid;
    gap: ${theme.spacing.x4};
    height: ${styleUtils.rem(256)};
    justify-items: center;
    padding-left: ${theme.spacing.x4};
    padding-right: ${theme.spacing.x4};
  `,
  disabledMessageWrapperMobile: css`
    height: ${styleUtils.rem(234)};
  `,
  disabledOverlay: css`
    background-color: rgba(51, 51, 51, 0.8);
    border-radius: ${styleUtils.rem(8)};
    bottom: 0;
    left: 0;
    padding-left: ${theme.spacing.x4};
    padding-right: ${theme.spacing.x4};
    position: absolute;
    right: 0;
    top: 0;
    z-index: 1;
  `,
  giftedBadge: css`
    left: ${theme.spacing.x2};
    position: absolute;
    top: ${theme.spacing.x2};
  `,
  giftedBadgeContent: css`
    align-items: center;
    display: flex;
    gap: ${theme.spacing.base};
  `,
  imageInner: css`
    height: 100%;
    width: 100%;

    & img {
      height: 100%;
      object-fit: cover;
      width: 100%;
    }
  `,
  imageWrapper: css`
    aspect-ratio: 1;
    border-top-left-radius: ${styleUtils.rem(8)};
    border-top-right-radius: ${styleUtils.rem(8)};
    cursor: pointer;
    overflow: hidden;
    position: relative;
    width: 100%;
  `,
  pollingOverlay: css`
    align-items: center;
    background-color: rgba(255, 255, 255, 0.7);
    bottom: 0;
    display: flex;
    justify-content: center;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 1;
  `,
  subtitle: css`
    ${theme.typeStyles.bodyExtraSmallStyles};
    color: ${theme.colors.textSecondary};
    display: flex;
  `,
  textBlock: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.base};
    padding-bottom: ${theme.spacing.x4};
    padding-left: ${theme.spacing.x4};
    padding-right: ${theme.spacing.x4};
    padding-top: ${theme.spacing.x4};
    text-align: left;
    ${theme.media.lessThan(theme.breakpoints.small)(`
      padding-bottom: ${theme.spacing.x3};
      padding-left: ${theme.spacing.x2};
      padding-right: ${theme.spacing.x2};
      padding-top: ${theme.spacing.x2};
    `)};
  `,
  title: css`
    display: flex;
    overflow-wrap: break-word;
    word-break: break-word;
    ${theme.typeStyles.titleSmallStyles};
  `,
};

type DisplayStripeRegistrySectionProps = {
  configuration: Configuration;
} & SectionComponentProps;

const DisplayStripeRegistrySection = ({
  configuration,
  section,
}: DisplayStripeRegistrySectionProps) => {
  const { isDemo, isPreview } = useIsPreviewOrDemo();
  const {
    pollContributionStatus,
    isStripeSetupComplete,
    isStripeOnboardingSubmitted,
  } = useGuestStripe();
  const details = section.sectionDetails as StripeRegistryDetails;
  const width = useWebsiteWidth();
  const isMobile: boolean = width !== null && width < 600;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPollingForContribution, setIsPollingForContribution] =
    useState(false);
  const [contributionBaselineCents, setContributionBaselineCents] = useState<
    number | null
  >(null);
  const [liveContributionTotalCents, setLiveContributionTotalCents] = useState<
    number | null
  >(null);

  const contributionTotalCents =
    liveContributionTotalCents ?? details.contributionTotalCents ?? 0;
  const goalMet = isStripeGoalMet(
    contributionTotalCents,
    details.goalAmountCents,
  );

  // No redirect-based confirmation handling: we use ui_mode=elements with
  // redirect-free payment methods only, so the thank-you screen is reached
  // inline after checkout.confirm() resolves inside the modal.

  const openModal = useCallback(() => {
    if (isDemo) {
      return;
    }
    setIsModalOpen(true);
  }, [isDemo]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handlePaymentComplete = useCallback(() => {
    // The webhook that updates contribution_total_cents lands asynchronously,
    // so poll for it rather than leaving the goal looking stale.
    setContributionBaselineCents(details.contributionTotalCents ?? 0);
    setIsPollingForContribution(true);
  }, [details.contributionTotalCents]);

  useEffect(() => {
    if (!isPollingForContribution || contributionBaselineCents === null) {
      return;
    }

    return pollContributionStatus(
      section.id,
      contributionBaselineCents,
      (totalCents) => {
        setLiveContributionTotalCents(totalCents);
        setIsPollingForContribution(false);
      },
    );
  }, [
    isPollingForContribution,
    contributionBaselineCents,
    pollContributionStatus,
    section.id,
  ]);

  if (!isPreview && !isStripeSetupComplete) {
    return null;
  }

  return (
    <>
      <div
        css={[styles.card(isMobile), themeTypographyResetStyles]}
        data-cy={selectors.stripeRegistry_websiteCard}
      >
        <div
          css={styles.imageWrapper}
          data-cy={selectors.stripeRegistry_websiteCard_image}
          onClick={openModal}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              openModal();
            }
          }}
          role="button"
          tabIndex={0}
        >
          <div css={styles.imageInner}>
            <DisplayPhotoOrVideo
              configuration={configuration}
              defaultImage={CashRegistryDefaultImage}
              isRegistryPhoto
              section={section}
            />
          </div>
          {goalMet && (
            <div
              css={styles.giftedBadge}
              data-cy={selectors.stripeRegistry_websiteCard_giftedBadge}
            >
              <Badge type="positiveLight">
                <div css={styles.giftedBadgeContent}>
                  <Icon size="extraSmall" type={Icon.types.check} />
                  Gifted
                </div>
              </Badge>
            </div>
          )}
        </div>

        <div
          css={styles.textBlock}
          data-cy={selectors.stripeRegistry_websiteCard_text}
          onClick={openModal}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              openModal();
            }
          }}
          role="button"
          tabIndex={0}
        >
          <div css={styles.subtitle}>Cash fund</div>
          {details.title && (
            <div
              css={styles.title}
              data-cy={selectors.stripeRegistry_websiteCard_title}
            >
              {details.title}
            </div>
          )}
          <StripeRegistryProgressBar
            dataCy={selectors.stripeRegistry_websiteCard_progressBar}
            fundedCents={contributionTotalCents}
            goalAmountCents={details.goalAmountCents}
            hideGoal={details.hideGoal}
          />
        </div>

        {!isStripeSetupComplete && (
          <div css={styles.disabledOverlay}>
            <div
              css={[
                styles.disabledMessageWrapper,
                isMobile && styles.disabledMessageWrapperMobile,
              ]}
            >
              <div css={styles.disabledTitle}>
                {isStripeOnboardingSubmitted
                  ? "We're verifying your account details with Stripe."
                  : "Finish Stripe setup to make your registry visible to guests."}
              </div>

              {isStripeOnboardingSubmitted && (
                <div css={styles.disabledBodyText}>
                  This usually doesn&apos;t take long — check back shortly.
                </div>
              )}
            </div>
          </div>
        )}

        {isPollingForContribution && (
          <div css={styles.pollingOverlay}>
            <LoadingSpinner loading />
          </div>
        )}
      </div>

      {isModalOpen && (
        <StripeContributionModal
          configuration={configuration}
          onClose={closeModal}
          onPaymentComplete={handlePaymentComplete}
          section={section}
        />
      )}
    </>
  );
};

const MemoizedDisplayStripeRegistrySection = memo(DisplayStripeRegistrySection);

export { MemoizedDisplayStripeRegistrySection as DisplayStripeRegistrySection };
